import i18n from '../../i18n/i18n';
import { tr } from '../../i18n/i18n';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { STATUS } from '../../constants';
import {
  ICardDetails,
  IClassroom,
  ICreateStudentError,
  ICreateStudentRequestBody,
  ICreateStudentResponseBody,
  IGetClassroomResponse,
  IGetGradesResponse,
  IGetMediumsResponse,
  IgetStudentDetailsRequestBodyParams,
  IGetStudentsResponseBody,
  IGrade,
  IStudent,
  IStudentData,
  IStudentGuardian,
  IStudentNextNumberResponseBody,
  IStudentSoftDeleteRequestBody,
  IStudentSoftDeleteResponseBody,
  IStudentUpdatePayload,
  IUpdateStudentDetailsResponseBodyParams,
  IBulkActivationResponse,
  IStudentActivationResult,
} from '../../interface/studentModel';
import { IGetAssetId } from '../../interface/teacherModel';
import api from '../../services/api';
import apiMultipart from '../../services/api-multipart';
import {
  createStudent,
  getClassroom,
  getClassroomById,
  getGrades,
  getNextId,
  getStudentData,
  softDeleteStudent,
  updateStudent,
} from '../../services/student/index';

import { adminDeleteStudent } from '../../services/classveew-admin/student/index';
import {
  fetchStudentById,
  getGuardiansForStudent,
  bulkActivateStudents,
  studentSendInvitation,
} from '../action/student.action';
import { RootState } from '../store/index';

// State interface
interface IStudentState {
  loading: boolean;
  classroomLoading?: boolean;
  error?: ICreateStudentError;
  message?: string;
  description?: string;
  status?: string;
  grade?: IGrade[];
  classrooms?: IClassroom[];
  studentData?: IStudentData[];
  totalStudents: number;
  cardDetails?: ICardDetails;
  profile: IStudent | null;
  guardians: IStudentGuardian[];
  bulkActivationLoading: boolean;
  bulkActivationError: string | null;
  bulkActivationResults: IStudentActivationResult[];
}

// Default state
const DEFAULT_STATE: IStudentState = {
  description: undefined,
  loading: false,
  classroomLoading: false,
  error: undefined,
  message: undefined,
  status: undefined,
  grade: [],
  studentData: [],
  totalStudents: 0,
  cardDetails: undefined,
  classrooms: [],
  profile: null,
  guardians: [],
  bulkActivationLoading: false,
  bulkActivationError: null,
  bulkActivationResults: [],
};

// Thunk for creating a student profile
export const createStudentProfile = createAsyncThunk<
  ICreateStudentResponseBody,
  ICreateStudentRequestBody,
  { rejectValue: ICreateStudentError }
>('student', async (body, { rejectWithValue }) => {
  try {
    const response = await createStudent(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred'))) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const updateStudentProfile = createAsyncThunk<
  IUpdateStudentDetailsResponseBodyParams,
  { body: IStudentUpdatePayload; id: string },
  { rejectValue: ICreateStudentError }
>('student/updateStudentData', async ({ body, id }, { rejectWithValue }) => {
  try {
    const response = await updateStudent(body, id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred'))) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});
// Thunk for fetching student data
export const fetchStudentData = createAsyncThunk<
  IGetStudentsResponseBody,
  { params?: IgetStudentDetailsRequestBodyParams } | undefined,
  { rejectValue: ICreateStudentError }
>('student/fetchStudentData', async ({ params }, { rejectWithValue }) => {
  try {
    const response = await getStudentData(params);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred')),
      });
    }
    return rejectWithValue({ status: 500, message: err.message || i18n.t('toast.unexpected_error', 'Unexpected error') });
  }
});

export const getStudentNextId = createAsyncThunk<
  IStudentNextNumberResponseBody, // Successful response type
  void, // Argument type (the `id` here)
  { rejectValue: ICreateStudentError } // Error type for rejected action
>('student/fetchStudentDataById', async (id, { rejectWithValue }) => {
  try {
    const response = await getNextId();
    return response as unknown as IStudentNextNumberResponseBody; // Return the actual data if the request succeeds
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.an_error_occurred', 'An error occurred') });
    } else {
      return rejectWithValue({ message: i18n.t('msg.an_unexpected_error_occurred', i18n.t('toast.an_unexpected_error_occurred', 'An unexpected error occurred')) });
    }
  }
});

export const fetchMediums = createAsyncThunk<IGetMediumsResponse, void>(
  'student/fetchMedium',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<IGetMediumsResponse>('/subject/medium');
      return response.data;
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const error = err as AxiosError<ICreateStudentError>;
        return rejectWithValue({
          status: error.response?.status || 500,
          message: error.response?.data?.message || i18n.t('toast.unknown_error', i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred')),
        });
      } else {
        return rejectWithValue({ status: err.status || 500, message: err.message });
      }
    }
  },
);

export const fetchClassroom = createAsyncThunk<
  IGetClassroomResponse,
  | {
      gradeIds?: string[];
      curriculumIds?: string[];
      isActive?: boolean;
      fetchAll?: boolean;
    }
  | void
>('student/fetch-classroom', async (params, { rejectWithValue }) => {
  try {
    const response = await getClassroom(params || undefined);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      return rejectWithValue(error.response?.data || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred'))) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const fetchClassroomById = createAsyncThunk<IGetClassroomResponse, string>(
  'student/fetchClassroom',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getClassroomById(id); // id can be undefined
      return response;
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const error = err as AxiosError<IGetClassroomResponse>;
        return rejectWithValue(error.response?.data || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred'))) });
      } else {
        return rejectWithValue({ message: err.message });
      }
    }
  },
);

// Thunk for soft deleting a student profile
export const deleteStudentProfile = createAsyncThunk<
  IStudentSoftDeleteResponseBody,
  IStudentSoftDeleteRequestBody,
  { rejectValue: ICreateStudentError }
>('student/deleteStudentProfile', async (body, { rejectWithValue }) => {
  try {
    const response = await softDeleteStudent(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred'))) });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

// Admin delete (root-admin) - requires orgId
export const adminDeleteStudentProfile = createAsyncThunk<
  IStudentSoftDeleteResponseBody,
  { id: string; orgId: string },
  { rejectValue: ICreateStudentError }
>('student/adminDeleteStudentProfile', async ({ id, orgId }, { rejectWithValue }) => {
  try {
    const response = await adminDeleteStudent(id, orgId);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateStudentError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: i18n.t('msg.an_unknown_error_occurred', 'An unknown error occurred') });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

// Slice for student state management
const studentSlice = createSlice({
  name: 'student',
  initialState: DEFAULT_STATE,
  reducers: {
    resetStudentState: () => DEFAULT_STATE,
    resetClassroomState: (state) => {
      state.classrooms = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createStudentProfile.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      createStudentProfile.fulfilled,
      (state, action: PayloadAction<ICreateStudentResponseBody>) => {
        state.loading = false;
        state.message = action.payload.message;
        state.status = action.payload.status;
      },
    );
    builder.addCase(
      createStudentProfile.rejected,
      (state, action: PayloadAction<ICreateStudentError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.description = action.payload?.description;
        state.status = STATUS.FAILED;
      },
    );
    builder.addCase(fetchStudentData.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      fetchStudentData.fulfilled,
      (state, action: PayloadAction<IGetStudentsResponseBody>) => {
        state.loading = false;
        state.studentData = action.payload.data?.students || [];
        state.totalStudents = action.payload.data?.total || 0;
        state.message = action.payload.message;
        state.cardDetails = action.payload.data?.cardDetails;
      },
    );
    builder.addCase(
      fetchStudentData.rejected,
      (state, action: PayloadAction<ICreateStudentError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.description = action.payload?.description;
        state.status = STATUS.FAILED;
      },
    );
    builder.addCase(fetchClassroom.pending, (state) => {
      state.classroomLoading = true;
      state.status = STATUS.LOADING;
      state.classrooms = [];
    });
    builder.addCase(
      fetchClassroom.fulfilled,
      (state, action: PayloadAction<IGetClassroomResponse>) => {
        state.classroomLoading = false;
        state.classrooms = action.payload.data.classrooms;
        state.message = action.payload.message;
      },
    );
    builder.addCase(
      fetchClassroom.rejected,
      (state, action: PayloadAction<ICreateStudentError | undefined>) => {
        state.classroomLoading = false;
        state.status = STATUS.FAILED;
        state.classrooms = [];
      },
    );
    builder.addCase(deleteStudentProfile.pending, (state) => {
      state.loading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      deleteStudentProfile.fulfilled,
      (state, action: PayloadAction<IStudentSoftDeleteResponseBody>) => {
        state.loading = false;
        state.message = action.payload.message;
        // state.status = STATUS.SUCCESS;
      },
    );
    builder.addCase(
      deleteStudentProfile.rejected,
      (state, action: PayloadAction<ICreateStudentError | undefined>) => {
        state.loading = false;
        state.error = action.payload;
        state.description = action.payload?.description;
        state.status = STATUS.FAILED;
      },
    );
    builder.addCase(fetchClassroomById.pending, (state) => {
      state.classroomLoading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      fetchClassroomById.fulfilled,
      (state, action: PayloadAction<IGetClassroomResponse>) => {
        state.classroomLoading = false;
        state.message = action.payload.message;
        state.classrooms = action.payload.data?.classrooms;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder
      .addCase(
        fetchClassroomById.rejected,
        (state, action: PayloadAction<ICreateStudentError | undefined>) => {
          state.classroomLoading = false;
          state.description = action.payload?.message;
          state.status = STATUS.FAILED;
          state.classrooms = [];
        },
      )

      .addCase(fetchStudentById.pending, (state) => {
        state.loading = true;
        state.profile = null; // Clear previous profile while loading
      })
      .addCase(fetchStudentById.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.data.student;
      })
      .addCase(fetchStudentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.profile = null;
        state.status = STATUS.FAILED;
      })

      .addCase(getGuardiansForStudent.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGuardiansForStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.guardians = action.payload.data.guardians;
      })
      .addCase(getGuardiansForStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.message = action.payload?.message || i18n.t('toast.failed_to_fetch_guardians', 'Failed to fetch guardians');
        state.guardians = [];
      })
      .addCase(bulkActivateStudents.pending, (state) => {
        state.bulkActivationLoading = true;
        state.bulkActivationError = null;
      })
      .addCase(
        bulkActivateStudents.fulfilled,
        (state, action: PayloadAction<IBulkActivationResponse>) => {
          state.bulkActivationLoading = false;
          // Store activation results directly from action.payload
          // IBulkActivationResponse contains: total_requested, total_activated, total_failed, activation_results
          state.bulkActivationResults = action.payload.activation_results;
        },
      )
      .addCase(
        bulkActivateStudents.rejected,
        (state, action: PayloadAction<ICreateStudentError | undefined>) => {
          state.bulkActivationLoading = false;
          state.bulkActivationError = action.payload?.message || i18n.t('toast.activation_failed', 'Activation failed');
        },
      )

      // Send Student Invitation (send / resend)
      .addCase(studentSendInvitation.pending, (state) => {
        state.loading = true;
        state.status = STATUS.LOADING;
      })
      .addCase(studentSendInvitation.fulfilled, (state, action) => {
        state.loading = false;
        state.status = STATUS.SUCCEEDED;
        state.message = action.payload.message;
      })
      .addCase(
        studentSendInvitation.rejected,
        (state, action: PayloadAction<ICreateStudentError | undefined>) => {
          state.loading = false;
          state.status = STATUS.FAILED;
          state.message = action.payload?.message || 'Failed to send invitations';
        },
      );
  },
});

// Export actions and reducer
export const { resetStudentState, resetClassroomState } = studentSlice.actions;
export const studentReducer = studentSlice.reducer;

// Selectors
export const selectStudentState = (state: RootState): IStudentState => state.student;
export const selectStudentStatus = (state: RootState): string | undefined => state.student.status;
export const selectStudentError = (state: RootState): ICreateStudentError | undefined =>
  state.student.error;
export const selectStudentMessage = (state: RootState): string | undefined => state.student.message;
export const selectGrade = (state: RootState): IGrade[] | undefined => state.student.grade;
export const selectGrades = (state: RootState): IClassroom[] | undefined =>
  state.student.classrooms;
export const selectStudentDescription = (state: RootState): string | undefined =>
  state.student.description;
export const selectStudentLoading = (state: RootState): boolean => state.student.loading;
export const selectClassroomLoading = (state: RootState): boolean =>
  !!state.student.classroomLoading;
export const selectStudentData = (state: RootState): IStudentData[] | undefined =>
  state.student.studentData;
export const selectCardDetails = (state: RootState): ICardDetails => state.student.cardDetails;

export default studentReducer;
